//@ts-strict-ignore
import { serviceActions } from '../state/modules/service';
import { useDispatch } from 'react-redux';
import { useCallback, useContext } from 'react';
import ConnectedFormContext from '../components/Context/ConnectedFormContext';
import { AlertType, Maybe } from '../graphql/schema/p4types';
import { Dispatch } from 'redux';
import { EntityType } from '../@types/entityTypes';
import { useP4ToastController } from '../views/system/NotificationsArea';

export const alertAction = ({ dispatch, entity }, meta, title, message, mutationProps?: any) => {
  const { status, confirmationReference, uuid, responses, errorId } = meta;
  dispatch(
    serviceActions.genericAlert(
      title,
      message,
      AlertType.Warning,
      {
        alertForm: entity?.type,
        status,
        confirmationReference,
        uuid,
        responses,
        mutationProps,
      },
      getSentryErrorId(status, errorId)
    )
  );
};

function handleFieldAlerts(error) {
  return Object.keys(error.violationErrors)
    .map((v) => ` ${v} - ${error.violationErrors[v].submit}`)
    .join('\n ');
}

export function handleAlerts(
  error,
  {
    dispatch,
    name,
    entity,
    customAlertHandler,
  }: { dispatch: Dispatch; name?: string; entity: Maybe<EntityType>; customAlertHandler?: Function },
  saveOrDelete: Maybe<'saved' | 'deleted' | 'completed'>,
  mutationProps?: any,
  overrideTitle: Maybe<string> = null
) {
  const violations = Object.keys(error.violationErrors);
  const { meta } = error;
  let message;
  if (violations.length > 0) {
    message = handleFieldAlerts(error);
  } else {
    if (meta && meta.status === 'FOREIGN_KEY_VIOLATION' && saveOrDelete === 'deleted') {
      message = meta?.message || 'The record you are attempting to delete is used elsewhere in the system';
    } else {
      const msg = saveOrDelete
        ? `${name} could not be ${saveOrDelete} due to an unexpected error`
        : 'An unexpected error occurred';
      message = meta && meta.message ? meta.message : msg;
    }

    if (meta && meta.status === 'UNEXPECTED' && meta.errorId) {
      message += `\nError ID: ${meta.errorId}`;
    }
  }

  const defaultTitle = saveOrDelete ? `${name} could not be ${saveOrDelete}` : 'Request Unsuccessful';
  const title = overrideTitle ? overrideTitle : defaultTitle;

  if (customAlertHandler) {
    customAlertHandler({ dispatch, entity }, meta, title, message, mutationProps);
  } else {
    alertAction({ dispatch, entity }, meta, title, message, mutationProps);
  }
}

const replaceTerms = (text) => {
  const terms = {
    BigDecimal: 'Numeric (Decimal)',
    Float: 'Numeric (Decimal)',
    Integer: 'Numeric (Whole)',
    Int: 'Numeric (Whole)',
    String: 'Text',
    integerValue: 'Attributes',
    dateValue: 'Attributes',
    textValue: 'Attributes',
    doubleValue: 'Attributes',
    Variables: 'Field',
    Variable: 'Field',
  };

  const re = new RegExp(
    Object.keys(terms)
      .map((term) => `\\b${term}\\b`)
      .join('|'),
    'gi'
  );
  const str = text.replace(/'/g, '');
  return str.replace(re, (matched) => {
    return terms[matched] ? terms[matched] : '';
  });
};

const parseErrorText = (message, description, field) => {
  let text = description ? description : message;
  if (text.includes('Detail:')) {
    text = text.includes('Detail: Failing') ? '' : text.substr(text.indexOf('Detail:'));
  }
  if (text === 'The given id must not be null!') {
    text = 'No record was provided';
  }
  text = replaceTerms(text);
  if (field) {
    const fieldId = field.replace(/_/g, ' ').replace(/ id/, '');
    text = `Field ${fieldId} is invalid. ${text}`;
  }
  return text;
};

const setErrorMetaData = (error, field?: string) => {
  const { message, description, errorType, confirmationReference, uuid, responses, errorId } = error;
  const text = parseErrorText(message, description, field);
  return {
    message: text,
    status: errorType,
    confirmationReference,
    uuid,
    responses,
    errorId,
  };
};

export const parseErrorResponse = (errors) => {
  const violationErrors = {};
  let meta = {};
  if (Array.isArray(errors)) {
    errors.forEach((error) => {
      if (error && error.violations && Object.keys(error.violations).length > 0) {
        Object.keys(error.violations).forEach((field) => {
          let text = error.violations[field];
          if (text === 'may not be null' || text === 'may not be empty') {
            text = 'may not be left blank';
          }
          if (error && error.errorType === 'UNIQUE_FIELD_VIOLATION') {
            text = `A record already exists with unique id/value: '${error.violations[field]}'`;
          }
          violationErrors[field] = {};
          // We use submit here as the validation type
          violationErrors[field].submit = text;
          meta = setErrorMetaData(error, field);
        });
      } else if (error && error.message) {
        meta = setErrorMetaData(error);
      }
    });
  }
  return { networkResponse: errors, violationErrors, meta };
};

//Hook to attach dispatch/entity to handleQueryComponentError call.
export const useComponentQueryErrorHandling = ({
  customAlertHandler,
  name,
  entity,
}: {
  customAlertHandler?: Function;
  name?: string;

  /**
   * Override entity - If 'connectedForm' is not available, may make sense to pass this in for better text on errors.
   */
  entity?: EntityType;
} = {}) => {
  const dispatch = useDispatch();
  const connectedForm = useContext(ConnectedFormContext);
  const { dispatchSimpleToast } = useP4ToastController();
  return useCallback(
    (data: any) => {
      handleComponentQueryError(data, {
        dispatch,
        name,
        entity: entity ?? connectedForm?.entity,
        customAlertHandler,
        dispatchSimpleToast,
      });
    },
    [dispatch, name, connectedForm]
  );
};

const getSentryErrorId = (errorType: string, errorId: any) => {
  return errorType === 'UNEXPECTED' || errorType === 'DATABASE_ERROR' ? errorId : null;
};

export const handleComponentQueryError = (
  data: any,
  {
    dispatch,
    name,
    entity,
    customAlertHandler,
    dispatchSimpleToast,
  }: {
    dispatch: Dispatch;
    name?: string;
    entity: Maybe<EntityType>;
    customAlertHandler?: Function;
    dispatchSimpleToast?: ReturnType<typeof useP4ToastController>['dispatchSimpleToast'];
  },
  overrideTitle: Maybe<string> = null
) => {
  const title = overrideTitle || 'Request Unsuccessful';

  if (data?.networkError?.result?.errors) {
    for (const error of data.networkError.result.errors) {
      const { errorType, errorId, message } = error;

      if (customAlertHandler) {
        customAlertHandler({}, error, title, message, {});
        return;
      }

      if (dispatchSimpleToast) {
        dispatchSimpleToast(title, error.message, AlertType.Warning, { errorId: error?.errorId });
      } else {
        dispatch(
          serviceActions.genericAlert(title, message, AlertType.Warning, {}, getSentryErrorId(errorType, errorId))
        );
      }
    }
  } else {
    const error = parseErrorResponse(data.errors);
    handleAlerts(error, { dispatch, name, entity, customAlertHandler }, null, null, title);
  }
};
